import type { CompanyActualVsBudget } from '@/features/actual-vs-budget-production/types'
import type { AxiosRequestConfig } from 'axios'
import { httpClient } from '@/configs/axios.config'
import type {
  AllowedApp,
  CreateOgsmActualInput,
  CreateOgsmNodeInput,
  CreateOgsmRevisionInput,
  OgsmActual,
  OgsmArchiveResult,
  OgsmAssignee,
  OgsmBoard,
  OgsmNode,
  OgsmRemovalImpact,
  OgsmSession,
  OgsmSessionList,
  UpdateOgsmNodeInput,
} from '@/types/api'
import type {
  CompanyFinancialsScenarioType,
  CompanyFinancialsView,
  CompanyFinancialsTrailingPeriod,
  CompanyFinancialsTableResponse,
} from '@/services/companies.service'
import type {
  CompanyMetricSeriesListResponseDto,
  GetCompanyMetricsSeriesParams,
  MetricLabelOptionsResponseDto,
} from '@/services/metrics.service'

const portalReadConfig: AxiosRequestConfig & {
  skipNotFoundRedirect: boolean
} = { skipNotFoundRedirect: true }

// =============================================================================
// Portal Types
// =============================================================================

export type PortalLocation = {
  country?: { id: string; name: string }
  state?: { id: string; name: string }
  city?: { id: string; name: string }
}

export type PortalIndustry = {
  id: string
  name: string
  isPrimary: boolean
}

export type PortalSector = {
  id: string
  name: string
  isPrimary: boolean
}

export type PortalBoardMember = {
  id: string
  name: string
}

export type PortalCompany = {
  id: string
  displayName: string
  legalName?: string | null
  description?: string | null
  status: 'ACTIVE' | 'EXITED' | 'ON_HOLD'
  location?: PortalLocation | null
  industries: PortalIndustry[]
  sectors: PortalSector[]
  boardMembers?: PortalBoardMember[]
  website?: string | null
  employeeCount?: number | null
  geographicFocus?: string | null
  fiscalYearEnd?: string | null
  ltmPeriodEndDate?: string | null
  financialsUpdatedAt?: string | null
}

export type PortalMetricPeriod = {
  period: string
  periodEndDate: string
  value: number
}

export type PortalMetricSeries = {
  metricType: string
  periodType: string
  dataType?: string
  periods: PortalMetricPeriod[]
}

export type PortalMetricsResponse = {
  companyId: string
  companyName: string
  metrics: PortalMetricSeries[]
}

export type PortalContact = {
  id: string
  firstName: string
  lastName?: string
  email?: string | null
  title?: string
  phone?: string | null
}

export type PortalContactsResponse = {
  data: PortalContact[]
}

export type CreatePortalContactRequest = {
  firstName: string
  lastName?: string
  email?: string
  phone?: string
  title: string
  isPrimaryContact?: boolean
}

export type UpdatePortalContactRequest = Partial<CreatePortalContactRequest>

export type PortalFundInvestment = {
  id: string
  abbreviation: string
  displayName: string
  investedAmount: number | null
  initialInvestmentDate: string | null
  investedThroughDate: string | null
  investmentStatus: 'ACTIVE' | 'EXITED' | null
  isPrimary: boolean
}

export type PortalFundInvestmentsResponse = {
  data: PortalFundInvestment[]
}

export type PortalCompanyTransactionType =
  | 'EQUITY_INVESTMENT'
  | 'VALUATION'
  | 'DISTRIBUTION'
  | 'CASH_FLOW'

export type PortalCompanyTransaction = {
  id: string
  fundId: string
  fundAbbreviation: string
  fundDisplayName: string
  type: PortalCompanyTransactionType
  subType: string | null
  date: string
  amount: number
  note: string | null
}

export type PortalCompanyTransactionsResponse = {
  data: PortalCompanyTransaction[]
  total: number
}

export type GetPortalCompanyTransactionsParams = {
  type?: PortalCompanyTransactionType
  limit?: number
}

/** Stored OGSM level of a PortCo assignment. */
export type OgsmAccess = 'READ_ONLY' | 'EDIT'

export type PortalUser = {
  id: string
  name: string
  email: string
  role: string
  allowedApps: AllowedApp[]
  isActive: boolean
  createdAt: string
  ogsmAccess: OgsmAccess
  /** The effective right: true for every PORTCO_ADMIN whatever `ogsmAccess` holds. */
  ogsmCanEdit: boolean
}

export type PortalUsersResponse = {
  data: PortalUser[]
}

export type PortalInvitation = {
  id: string
  emailAddress: string
  status: string
  createdAt: number
  invitedRole?: string | null
  allowedApps: AllowedApp[]
  companyId?: string | null
  companyName?: string | null
}

export type PortalInvitationsResponse = {
  data: PortalInvitation[]
}

export type InviteUserRequest = {
  email: string
  companyId?: string
  role?: string
  allowedApps: AllowedApp[]
  /** Omitted: the backend stores READ_ONLY. */
  ogsmAccess?: OgsmAccess
}

export type InviteResponse = {
  email: string
  companyId: string
  companyName: string
  status: 'invited'
}

// =============================================================================
// Portal API
// =============================================================================

/**
 * Get the portal company for the current user
 */
export async function getPortalCompany(): Promise<PortalCompany> {
  // A user without an active company assignment gets a 404 here. Opt out of
  // the global 404 redirect so the portal renders its own empty state instead
  // of bouncing to /not-found (which loops back through /auth/redirect).
  const response = await httpClient.get<PortalCompany>(
    '/portal/company',
    portalReadConfig
  )
  return response.data
}

/**
 * Get financial metrics for the portal company
 */
export async function getPortalMetrics(
  periodType?: string
): Promise<PortalMetricsResponse> {
  const response = await httpClient.get<PortalMetricsResponse>(
    '/portal/company/metrics',
    { params: periodType ? { periodType } : undefined }
  )
  return response.data
}

/**
 * Get contacts for the portal company
 */
export async function getPortalContacts(): Promise<PortalContactsResponse> {
  const response = await httpClient.get<PortalContactsResponse>(
    '/portal/company/contacts'
  )
  return response.data
}

/**
 * Create a contact for the portal company.
 * The backend derives company scope from the authenticated portal assignment;
 * the browser never sends or selects a tenant id.
 */
export async function createPortalContact(
  data: CreatePortalContactRequest
): Promise<PortalContact> {
  const response = await httpClient.post<PortalContact>(
    '/portal/company/contacts',
    data
  )
  return response.data
}

/**
 * Update a contact assigned to the portal company.
 * Tenant scope is resolved exclusively by the portal backend guard.
 */
export async function updatePortalContact(
  contactId: string,
  data: UpdatePortalContactRequest
): Promise<PortalContact> {
  const response = await httpClient.patch<PortalContact>(
    `/portal/company/contacts/${contactId}`,
    data
  )
  return response.data
}

/**
 * Remove a contact's assignment to the portal company.
 * The server removes only the assignment to the derived portal company.
 */
export async function deletePortalContact(contactId: string): Promise<void> {
  await httpClient.delete(`/portal/company/contacts/${contactId}`)
}

/**
 * Invite a user to the portal company
 */
export async function invitePortalUser(
  data: InviteUserRequest
): Promise<InviteResponse> {
  const response = await httpClient.post<InviteResponse>('/portal/invite', data)
  return response.data
}

/**
 * Get metric series for the portal company (same shape as internal metrics endpoint).
 */
export async function getPortalMetricsSeries(
  params: GetCompanyMetricsSeriesParams
): Promise<CompanyMetricSeriesListResponseDto> {
  const response = await httpClient.get<CompanyMetricSeriesListResponseDto>(
    '/portal/company/metrics/series',
    {
      params: {
        ...params,
        metricLabelIds:
          params.metricLabelIds && params.metricLabelIds.length > 0
            ? params.metricLabelIds
            : undefined,
      },
      paramsSerializer: { indexes: null },
    }
  )
  return response.data
}

/**
 * Get metric label options for the portal company.
 */
export async function getPortalMetricLabelOptions(params?: {
  periodType?: string
  scenarioType?: string
}): Promise<MetricLabelOptionsResponseDto> {
  const response = await httpClient.get<MetricLabelOptionsResponseDto>(
    '/portal/company/metrics/labels',
    { params }
  )
  return response.data
}

/**
 * Get the financials table for the portal company.
 */
export async function getPortalFinancialsTable(
  params: {
    scenarioType?: CompanyFinancialsScenarioType
    view?: CompanyFinancialsView
    trailingPeriod?: CompanyFinancialsTrailingPeriod
    showBlankPeriods?: boolean
    asOfDate?: string
    months?: number
    quarters?: number
    years?: number
  } = {}
): Promise<CompanyFinancialsTableResponse> {
  const response = await httpClient.get<CompanyFinancialsTableResponse>(
    '/portal/company/financials',
    { params }
  )
  return response.data
}

/**
 * Get fund investments for the portal company (sensitive fields excluded).
 */
export async function getPortalFundInvestments(): Promise<PortalFundInvestmentsResponse> {
  const response = await httpClient.get<PortalFundInvestmentsResponse>(
    '/portal/company/fund-investments'
  )
  return response.data
}

export async function getPortalCompanyTransactions(
  params: GetPortalCompanyTransactionsParams = {}
): Promise<PortalCompanyTransactionsResponse> {
  const response = await httpClient.get<PortalCompanyTransactionsResponse>(
    '/portal/company/transactions',
    { params }
  )
  return response.data
}

/**
 * Get users assigned to the portal company.
 */
export async function getPortalUsers(
  companyId?: string
): Promise<PortalUsersResponse> {
  const response = await httpClient.get<PortalUsersResponse>(
    '/portal/company/users',
    { params: companyId ? { companyId } : undefined }
  )
  return response.data
}

/**
 * Change a portal user's role.
 */
export async function changePortalUserRole(
  userId: string,
  role: string,
  companyId?: string
): Promise<PortalUser> {
  const response = await httpClient.patch<PortalUser>(
    `/portal/company/users/${userId}/role`,
    { role },
    // The edit sheets show the backend message inline.
    { params: companyId ? { companyId } : undefined, skipErrorToast: true }
  )
  return response.data
}

export async function changePortalUserAccess(
  userId: string,
  allowedApps: AllowedApp[],
  companyId?: string
): Promise<PortalUser> {
  const response = await httpClient.patch<PortalUser>(
    `/portal/company/users/${userId}/access`,
    { allowedApps },
    { params: companyId ? { companyId } : undefined, skipErrorToast: true }
  )
  return response.data
}

export async function changePortalUserOgsmAccess(
  userId: string,
  ogsmAccess: OgsmAccess,
  companyId?: string
): Promise<PortalUser> {
  const response = await httpClient.patch<PortalUser>(
    `/portal/company/users/${userId}/ogsm-access`,
    { ogsmAccess },
    { params: companyId ? { companyId } : undefined, skipErrorToast: true }
  )
  return response.data
}

/**
 * Remove a user from the portal company.
 */
export async function removePortalUser(
  userId: string,
  companyId?: string
): Promise<void> {
  await httpClient.delete(`/portal/company/users/${userId}`, {
    params: companyId ? { companyId } : undefined,
  })
}

/**
 * Get pending invitations for the portal company.
 */
export async function getPortalInvitations(
  companyId?: string
): Promise<PortalInvitationsResponse> {
  const response = await httpClient.get<PortalInvitationsResponse>(
    '/portal/invitations',
    { params: companyId ? { companyId } : undefined }
  )
  return response.data
}

/**
 * Get ALL pending invitations across all companies (admin).
 */
export async function getAllPortalInvitations(): Promise<PortalInvitationsResponse> {
  const response = await httpClient.get<PortalInvitationsResponse>(
    '/portal/invitations/all'
  )
  return response.data
}

/**
 * Revoke a pending invitation.
 */
export async function revokePortalInvitation(
  invitationId: string,
  companyId?: string
): Promise<void> {
  await httpClient.delete(`/portal/invitations/${invitationId}`, {
    params: companyId ? { companyId } : undefined,
  })
}

export async function resendPortalInvitation(
  invitationId: string,
  companyId?: string
): Promise<{ email: string; status: 'resent' }> {
  const response = await httpClient.post<{
    email: string
    status: 'resent'
  }>(`/portal/invitations/${invitationId}/resend`, undefined, {
    params: companyId ? { companyId } : undefined,
  })
  return response.data
}

export async function getPortalActualVsBudget(): Promise<
  Omit<CompanyActualVsBudget, 'funds'>
> {
  const response = await httpClient.get<Omit<CompanyActualVsBudget, 'funds'>>(
    '/portal/company/actual-vs-budget',
    { params: { period: 'FYTD' } }
  )
  return response.data
}

// =============================================================================
// Portal OGSM (mirrors services/ogsm.service.ts; the backend scopes the company)
// =============================================================================

const PORTAL_OGSM = '/portal/company/ogsm'

export async function listPortalOgsmSessions(): Promise<OgsmSessionList> {
  const { data } = await httpClient.get<OgsmSessionList>(
    `${PORTAL_OGSM}/sessions`,
    portalReadConfig
  )
  return data
}

/** The backend picks the year: the company's current fiscal year. */
export async function createPortalOgsmSession(): Promise<OgsmSession> {
  const { data } = await httpClient.post<OgsmSession>(
    `${PORTAL_OGSM}/sessions`,
    {}
  )
  return data
}

export async function getPortalOgsmBoard(
  sessionId: string
): Promise<OgsmBoard> {
  const { data } = await httpClient.get<OgsmBoard>(
    `${PORTAL_OGSM}/sessions/${sessionId}`,
    portalReadConfig
  )
  return data
}

export async function updatePortalOgsmStatement(
  sessionId: string,
  statement: string | null
): Promise<OgsmSession> {
  const { data } = await httpClient.patch<OgsmSession>(
    `${PORTAL_OGSM}/sessions/${sessionId}`,
    { statement },
    // The statement editor shows the backend message inline (ogsmErrorMessage).
    { skipErrorToast: true }
  )
  return data
}

export async function createPortalOgsmNode(
  sessionId: string,
  input: CreateOgsmNodeInput
): Promise<OgsmNode> {
  const { data } = await httpClient.post<OgsmNode>(
    `${PORTAL_OGSM}/sessions/${sessionId}/nodes`,
    input,
    // Every caller shows the backend message inline (ogsmErrorMessage).
    { skipErrorToast: true }
  )
  return data
}

export async function updatePortalOgsmNode(
  nodeId: string,
  input: UpdateOgsmNodeInput
): Promise<OgsmNode> {
  const { data } = await httpClient.patch<OgsmNode>(
    `${PORTAL_OGSM}/nodes/${nodeId}`,
    input
  )
  return data
}

export async function deletePortalOgsmNode(nodeId: string): Promise<void> {
  // DeleteNodeDialog shows every failure inline.
  await httpClient.delete(`${PORTAL_OGSM}/nodes/${nodeId}`, {
    skipErrorToast: true,
  })
}

/** What a removal would do: DELETE (no data), ARCHIVE (has data) or BLOCKED (with why). */
export async function getPortalOgsmRemovalImpact(
  nodeId: string
): Promise<OgsmRemovalImpact> {
  const { data } = await httpClient.get<OgsmRemovalImpact>(
    `${PORTAL_OGSM}/nodes/${nodeId}/removal-impact`,
    // DeleteNodeDialog shows a failed or refused read inline.
    { ...portalReadConfig, skipErrorToast: true }
  )
  return data
}

/** Takes a node with recorded data off the board; its history is kept. */
export async function archivePortalOgsmNode(
  nodeId: string
): Promise<OgsmArchiveResult> {
  const { data } = await httpClient.post<OgsmArchiveResult>(
    `${PORTAL_OGSM}/nodes/${nodeId}/archive`,
    undefined,
    // DeleteNodeDialog shows every failure inline.
    { skipErrorToast: true }
  )
  return data
}

export async function movePortalOgsmNode(
  nodeId: string,
  position: number
): Promise<OgsmNode> {
  const { data } = await httpClient.post<OgsmNode>(
    `${PORTAL_OGSM}/nodes/${nodeId}/move`,
    { position },
    // The board shows the backend message under the moved node (ogsmErrorMessage).
    { skipErrorToast: true }
  )
  return data
}

export async function listPortalOgsmAssignees(): Promise<OgsmAssignee[]> {
  const { data } = await httpClient.get<OgsmAssignee[]>(
    `${PORTAL_OGSM}/assignees`,
    portalReadConfig
  )
  return data
}

/** Newest first. */
export async function listPortalOgsmActuals(
  nodeId: string
): Promise<OgsmActual[]> {
  const { data } = await httpClient.get<OgsmActual[]>(
    `${PORTAL_OGSM}/nodes/${nodeId}/actuals`,
    // Both callers show a failed history inline (ActualHistory).
    { ...portalReadConfig, skipErrorToast: true }
  )
  return data
}

export async function addPortalOgsmActual(
  nodeId: string,
  input: CreateOgsmActualInput
): Promise<OgsmActual> {
  const { data } = await httpClient.post<OgsmActual>(
    `${PORTAL_OGSM}/nodes/${nodeId}/actuals`,
    input,
    // Every caller shows the backend message inline (ogsmErrorMessage).
    { skipErrorToast: true }
  )
  return data
}

export async function revisePortalOgsmTarget(
  nodeId: string,
  input: CreateOgsmRevisionInput
): Promise<OgsmNode> {
  const { data } = await httpClient.post<OgsmNode>(
    `${PORTAL_OGSM}/nodes/${nodeId}/revisions`,
    input
  )
  return data
}
